/**
 * Treatment Step Indicator
 *
 * Horizontal scrollable step navigation for the treatment wizard.
 * Shows step status (completed, active, skipped, pending, locked)
 * as compact pills with connecting lines and auto-scroll to the active step.
 */

import React, { useRef, useCallback } from "react";
import { View, Pressable, ScrollView } from "react-native";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { Text } from "@louise/ui/Text/Text";
import { Check, Slash } from "lucide-react-native";
import type { TreatmentStepConfig } from "./TreatmentProgressModal";
import type { TreatmentStepProgress } from "@louise/care/treatment-phases";

// Color constants
const greenAccent = "#4ADE80";
const violetAccent = "#C4B5FD";

export type StepStatus =
  "completed" | "active" | "skipped" | "pending" | "locked";

type PillLayout = { x: number; width: number };

export type TreatmentStepIndicatorProps = {
  steps: TreatmentStepConfig[];
  stepProgress: TreatmentStepProgress[] | null;
  activeStepIndex: number;
  firstPendingIndex: number;
  onStepPress: (index: number) => void;
};

function getStepStatus(
  index: number,
  stepId: string,
  activeStepIndex: number,
  firstPendingIndex: number,
  stepProgress: TreatmentStepProgress[] | null,
): StepStatus {
  const sp = stepProgress?.find((s) => s.stepId === stepId);

  if (sp?.status === "completed") return "completed";
  if (sp?.status === "skipped") return "skipped";
  if (index === activeStepIndex) return "active";
  if (index > firstPendingIndex) return "locked";
  return "pending";
}

export function TreatmentStepIndicator({
  steps,
  stepProgress,
  activeStepIndex,
  firstPendingIndex,
  onStepPress,
}: TreatmentStepIndicatorProps) {
  const capturePress = usePressCapture("TreatmentStepIndicator");
  const scrollViewRef = useRef<ScrollView>(null);
  const pillLayouts = useRef<PillLayout[]>([]);
  const scrollViewWidth = useRef(0);
  const hasInitialScrolled = useRef(false);

  const scrollToIndex = useCallback((index: number, animated: boolean) => {
    const layout = pillLayouts.current[index];
    const scrollView = scrollViewRef.current;
    const containerWidth = scrollViewWidth.current;

    if (!layout || !scrollView || !containerWidth) return;

    const pillCenter = layout.x + layout.width / 2;
    const scrollX = pillCenter - containerWidth / 3;

    scrollView.scrollTo({ x: Math.max(0, scrollX), animated });
  }, []);

  const tryInitialScroll = useCallback(() => {
    if (hasInitialScrolled.current) return;
    if (!scrollViewWidth.current) return;
    if (!pillLayouts.current[activeStepIndex]) return;

    hasInitialScrolled.current = true;
    scrollToIndex(activeStepIndex, false);
  }, [activeStepIndex, scrollToIndex]);

  const handleStepPress = useCallback(
    (index: number) => {
      if (index > firstPendingIndex) return; // locked
      scrollToIndex(index, true);
      onStepPress(index);
    },
    [firstPendingIndex, scrollToIndex, onStepPress],
  );

  return (
    <View
      style={{
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: "rgba(255, 255, 255, 0.05)",
      }}
    >
      <ScrollView
        ref={scrollViewRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 16,
          gap: 6,
          alignItems: "center",
        }}
        onLayout={(e) => {
          scrollViewWidth.current = e.nativeEvent.layout.width;
          tryInitialScroll();
        }}
      >
        {steps.map((step, index) => {
          const status = getStepStatus(
            index,
            step.id,
            activeStepIndex,
            firstPendingIndex,
            stepProgress,
          );

          const isTappable = index <= firstPendingIndex;

          // Pill styles by status
          const pillBg = {
            completed: "rgba(74, 222, 128, 0.1)",
            active: "rgba(196, 181, 253, 0.12)",
            skipped: "rgba(100, 116, 139, 0.08)",
            pending: "rgba(255, 255, 255, 0.03)",
            locked: "rgba(255, 255, 255, 0.02)",
          }[status];

          const pillBorder = {
            completed: "rgba(74, 222, 128, 0.25)",
            active: "rgba(196, 181, 253, 0.35)",
            skipped: "rgba(100, 116, 139, 0.2)",
            pending: "rgba(255, 255, 255, 0.08)",
            locked: "rgba(255, 255, 255, 0.04)",
          }[status];

          const labelColor = {
            completed: greenAccent,
            active: "#ffffff",
            skipped: "#64748b",
            pending: "#94a3b8",
            locked: "#475569",
          }[status];

          const numberColor = {
            completed: greenAccent,
            active: violetAccent,
            skipped: "#64748b",
            pending: "#64748b",
            locked: "#334155",
          }[status];

          return (
            <Pressable
              key={step.id}
              onLayout={(e) => {
                const { x, width } = e.nativeEvent.layout;
                pillLayouts.current[index] = { x, width };
                tryInitialScroll();
              }}
              onPress={() => {
                capturePress(pressLabel(step.title));
                handleStepPress(index);
              }}
              disabled={!isTappable}
              style={({ pressed }) => ({
                flexDirection: "row",
                alignItems: "center",
                gap: 7,
                paddingVertical: 8,
                paddingHorizontal: 12,
                borderRadius: 20,
                backgroundColor: pillBg,
                borderWidth: 1,
                borderColor: pillBorder,
                opacity:
                  status === "locked" ? 0.45 : pressed && isTappable ? 0.7 : 1,
                ...(status === "active"
                  ? {
                      shadowColor: violetAccent,
                      shadowOffset: { width: 0, height: 0 },
                      shadowOpacity: 0.2,
                      shadowRadius: 8,
                    }
                  : {}),
              })}
            >
              {/* Status indicator */}
              {status === "completed" ? (
                <View
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 9,
                    backgroundColor: "rgba(74, 222, 128, 0.2)",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Check size={11} color={greenAccent} strokeWidth={3} />
                </View>
              ) : status === "skipped" ? (
                <View
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 9,
                    backgroundColor: "rgba(100, 116, 139, 0.15)",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Slash size={10} color="#64748b" strokeWidth={2.5} />
                </View>
              ) : (
                <Text
                  style={{
                    fontSize: 11,
                    fontWeight: "700",
                    color: numberColor,
                    width: 18,
                    textAlign: "center",
                  }}
                >
                  {index + 1}
                </Text>
              )}

              {/* Label */}
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: status === "active" ? "600" : "500",
                  color: labelColor,
                }}
                numberOfLines={1}
              >
                {step.title}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}
